import {
    normalizeStructuredPayload,
    stripQueryMetadataFromText,
    tryParseStructuredPayloadFromText
} from 'helpers/copilotStructuredPayload';
import endpoints, { formatEndpoint } from '../endpoints';
import authHelper from 'helpers/authHelper';
import localStorageHelper from 'helpers/localStorageHelper';
import appConfig from 'config/appConfig';
import { getEndpointHeaders, getUserHeaders } from '../RestManager/headerMapper';
import { CHAT_STRATEGY, IGNORED_AGUI_EVENT_TYPES, INPUT_GUARDRAIL_BLOCKED_EVENT, MAX_SSE_BUFFER_SIZE, OUTPUT_GUARDRAIL_BLOCKED_EVENT } from './constants';

const DONE_SENTINEL = '[DONE]';

const generateUuid = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : r & 0x3 | 0x8;
        return v.toString(16);
    });
};

const safeTrim = (value) => typeof value === 'string' ? value.trim() : '';

const getAguiEndpoint = () => {
    const endpoint = formatEndpoint(endpoints.copilotKit.aguiEvents);
    return new URL(endpoint.url, appConfig.apiUrlWithoutBase).toString();
};

const getAguiHeaders = () => {
    const endpoint = formatEndpoint(endpoints.copilotKit.aguiEvents);
    const user = localStorageHelper.getItem('user');
    const accessToken = authHelper.getAccessToken();

    return {
        ...getEndpointHeaders(endpoint),
        ...getUserHeaders(user, accessToken),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
    };
};

const normalizeMessages = (messages) => {
    if (!Array.isArray(messages)) {
        return [];
    }

    return messages
        .map(({ id, role, content }) => ({
            id: safeTrim(id) || generateUuid(),
            role,
            content: typeof content === 'string' ? content : ''
        }))
        .filter(({ role, content }) => safeTrim(role).length > 0 && safeTrim(content).length > 0);
};

const getStructuredPayload = (parsedData) => {
    return normalizeStructuredPayload(parsedData);
};

const getNestedStructuredPayload = (parsedData) => {
    const nestedPayloadCandidates = [
        parsedData?.final,
        parsedData?.content,
        parsedData?.message,
        parsedData?.text,
        parsedData?.delta
    ];

    for (const candidate of nestedPayloadCandidates) {
        if (typeof candidate !== 'string') {
            continue;
        }

        const trimmedCandidate = safeTrim(candidate);
        if (!trimmedCandidate.startsWith('{') || !trimmedCandidate.endsWith('}')) {
            continue;
        }

        try {
            const structuredPayload = tryParseStructuredPayloadFromText(trimmedCandidate);
            if (structuredPayload) {
                return {
                    ...structuredPayload,
                    fallbackText: structuredPayload.fallbackText || trimmedCandidate
                };
            }
        } catch (error) {
            // Keep iterating through candidates and fall back to text if needed.
        }
    }

    return null;
};

const isIgnoredAguiEvent = (parsedData) => IGNORED_AGUI_EVENT_TYPES.includes(parsedData?.type);

const getTextPayload = (parsedData) => {
    const textDelta = parsedData?.delta ?? parsedData?.content ?? parsedData?.message ?? parsedData?.text ?? '';
    if (typeof textDelta !== 'string' || textDelta.length === 0) {
        return null;
    }

    return {
        type: 'text',
        delta: textDelta
    };
};

const toStringList = (value) => Array.isArray(value)
    ? value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
    : [];

const getCustomEventPayload = (parsedData) => {
    if (parsedData?.type !== 'CUSTOM') {
        return null;
    }

    const eventName = safeTrim(parsedData?.name);
    const value = parsedData?.value;
    if (!value || typeof value !== 'object') {
        return null;
    }

    if (eventName === 'RESPONSE_SOURCES') {
        const sources = toStringList(value.sources);
        const urls = toStringList(value.urls);
        if (sources.length === 0 && urls.length === 0) {
            return null;
        }

        return { type: 'sources', sources, urls };
    }

    if (eventName === 'RESPONSE_FOLLOWUPS') {
        const followups = toStringList(value.followup_questions);
        if (followups.length === 0) {
            return null;
        }

        return { type: 'followups', followups };
    }

    // guardrail rejections arrive as a CUSTOM event instead of a plain text message.
    if (eventName === INPUT_GUARDRAIL_BLOCKED_EVENT || eventName === OUTPUT_GUARDRAIL_BLOCKED_EVENT) {
        const blockedMessage = safeTrim(value.message);
        if (!blockedMessage) {
            return null;
        }

        return { type: 'text', delta: blockedMessage };
    }

    return null;
};

const parseSseDataChunk = (chunk) => {
    const events = [];
    const lines = chunk.split('\n');

    lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine.startsWith('data:')) {
            return;
        }

        const data = safeTrim(trimmedLine.replace(/^data:\s*/, ''));
        if (!data || data === DONE_SENTINEL) {
            return;
        }

        try {
            const parsed = JSON.parse(data);
            if (isIgnoredAguiEvent(parsed)) {
                return;
            }

            const customPayload = getCustomEventPayload(parsed);
            if (customPayload) {
                events.push(customPayload);
                return;
            }

            const structuredPayload = getStructuredPayload(parsed);
            if (structuredPayload) {
                events.push(structuredPayload);
                return;
            }

            const nestedStructuredPayload = getNestedStructuredPayload(parsed);
            if (nestedStructuredPayload) {
                events.push(nestedStructuredPayload);
                return;
            }

            const textPayload = getTextPayload(parsed);
            if (textPayload) {
                events.push(textPayload);
            }
        } catch (error) {
            events.push({
                type: 'text',
                delta: data
            });
        }
    });

    return events;
};

const toChunkEvents = (chunk) => {
    if (!safeTrim(chunk)) {
        return [];
    }

    if (chunk.includes('data:')) {
        return parseSseDataChunk(chunk);
    }

    try {
        const parsedData = JSON.parse(chunk);
        if (isIgnoredAguiEvent(parsedData)) {
            return [];
        }

        const customPayload = getCustomEventPayload(parsedData);
        if (customPayload) {
            return [customPayload];
        }

        const structuredPayload = getStructuredPayload(parsedData);
        if (structuredPayload) {
            return [structuredPayload];
        }

        const nestedStructuredPayload = getNestedStructuredPayload(parsedData);
        if (nestedStructuredPayload) {
            return [nestedStructuredPayload];
        }

        const textPayload = getTextPayload(parsedData);
        if (textPayload) {
            return [textPayload];
        }
    } catch (error) {
        // fall through to raw text event
    }

    return [{
        type: 'text',
        delta: chunk
    }];
};

const SSE_EVENT_BOUNDARY = /\r?\n\r?\n/;

const consumeSseStream = async (responseBody, onEvent, signal) => {
    const decoder = new TextDecoder();
    const reader = responseBody.getReader();
    let buffer = '';
    const cancelReader = () => {
        reader.cancel().catch(() => undefined);
    };

    signal?.addEventListener('abort', cancelReader, { once: true });

    try {
        while (!signal?.aborted) {
            const { done, value } = await reader.read();
            buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });

            if (buffer.length > MAX_SSE_BUFFER_SIZE) {
                throw new Error('Chat response exceeded the maximum stream buffer size.');
            }

            let boundaryMatch = SSE_EVENT_BOUNDARY.exec(buffer);
            while (boundaryMatch) {
                const rawEvent = buffer.slice(0, boundaryMatch.index);
                buffer = buffer.slice(boundaryMatch.index + boundaryMatch[0].length);

                if (safeTrim(rawEvent).length > 0) {
                    onEvent(rawEvent);
                }

                boundaryMatch = SSE_EVENT_BOUNDARY.exec(buffer);
            }

            if (done) {
                break;
            }
        }

        if (!signal?.aborted && safeTrim(buffer).length > 0) {
            onEvent(buffer);
        }
    } finally {
        signal?.removeEventListener('abort', cancelReader);
        reader.releaseLock?.();
    }
};

class CopilotKitApi {
    async streamDemoChat({ threadId, runId, messages = [], signal, onChunk }) {
        const normalizedMessages = normalizeMessages(messages);
        const resolvedThreadId = safeTrim(threadId) || generateUuid();
        const resolvedRunId = safeTrim(runId) || generateUuid();
        const aguiEndpoints = [getAguiEndpoint()];
        let lastError = null;

        for (const endpoint of aguiEndpoints) {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: getAguiHeaders(),
                body: JSON.stringify({
                    threadId: resolvedThreadId,
                    runId: resolvedRunId,
                    state: {},
                    messages: normalizedMessages,
                    tools: [],
                    context: [],
                    forwardedProps: { strategy: CHAT_STRATEGY }
                }),
                signal
            });

            if (!response.ok) {
                const errorBody = await response.text();
                lastError = new Error(errorBody || `Chat request failed with status ${response.status}`);

                throw lastError;
            }

            if (!response.body) {
                const text = await response.text();
                if (safeTrim(text).length > 0) {
                    onChunk?.(text);
                }
                return text;
            }

            let completeResponse = '';
            const emitEvents = (rawEvent) => {
                toChunkEvents(rawEvent).forEach((eventPayload) => {
                    if (eventPayload?.type === 'structured' || eventPayload?.type === 'sources' || eventPayload?.type === 'followups') {
                        onChunk?.(eventPayload);
                        if (eventPayload?.type === 'structured' && safeTrim(eventPayload.fallbackText).length > 0) {
                            completeResponse += eventPayload.fallbackText;
                        }
                        return;
                    }

                    completeResponse += eventPayload.delta;
                    onChunk?.(eventPayload.delta);
                });
            };

            await consumeSseStream(response.body, emitEvents, signal);

            return stripQueryMetadataFromText(completeResponse);
        }

        throw lastError || new Error('Chat request failed: no reachable endpoint configured.');
    }
}

export default new CopilotKitApi();
